// OrgDirectoryContext.jsx
// Session-scoped cache of the live Graph org directory. Fetched once per
// browser session (on OrgDirectoryProvider mount, when an accessToken is
// first available) and cached in sessionStorage -- deliberately NOT
// refreshed mid-session even if the directory changes in Azure AD while
// someone has the app open. That's an explicit design decision (stated
// in docs/superpowers/specs/2026-07-09-graph-user-directory-design.md),
// not an accident: a page refresh picks up changes, live polling does not
// exist here.
import { createContext, useContext, useState, useEffect } from "react";
import { fetchOrgUsers } from "./graphService";

const SESSION_KEY = "tk_org_directory_v1";

const OrgDirectoryContext = createContext({ orgUsers: [], loading: true, error: null });

export function OrgDirectoryProvider({ accessToken, children }) {
  const [state, setState] = useState(function () {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) return { orgUsers: JSON.parse(cached), loading: false, error: null };
    } catch (e) {
      // ignore parse/storage errors, fall through to a fresh fetch
    }
    return { orgUsers: [], loading: true, error: null };
  });

  useEffect(function () {
    // Check the cache BEFORE looking at accessToken. accessToken is always
    // null on the first render of every fresh mount (App.js acquires it
    // asynchronously) -- if this effect looked at accessToken first, it
    // would clobber the useState initializer's just-hydrated cached state
    // back to empty on every reload after the first, and (since the
    // alreadyCached branch below only ever returns early, never restores
    // state) that clobber could never self-correct once the real token
    // arrived a moment later. Checking the cache first means an already-
    // cached session is never touched again, regardless of accessToken's
    // value or timing.
    let alreadyCached;
    try {
      alreadyCached = sessionStorage.getItem(SESSION_KEY);
    } catch (e) {
      alreadyCached = null;
    }
    if (alreadyCached) return; // session-scoped cache already populated, no mid-session refresh

    if (!accessToken) {
      setState({ orgUsers: [], loading: false, error: null });
      return;
    }

    fetchOrgUsers(accessToken)
      .then(function (orgUsers) {
        setState({ orgUsers: orgUsers, loading: false, error: null });
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(orgUsers));
        } catch (e) {
          // sessionStorage full or unavailable -- state is still set in memory for this render
        }
      })
      .catch(function (err) {
        setState({ orgUsers: [], loading: false, error: err.message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return <OrgDirectoryContext.Provider value={state}>{children}</OrgDirectoryContext.Provider>;
}

export function useOrgDirectory() {
  return useContext(OrgDirectoryContext);
}
